import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  IconButton,
  Badge,
  HStack
} from "@chakra-ui/react";
import { ArrowRight, Copy, Check, ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import MarkdownRenderer from './MarkdownRenderer';
import { useState } from 'react';

export default function AdminLeftPane({
  messagesContainerRef,
  turns,
  activeSuggestionTurnId,
  onScrollMessages,
  isRightPaneOpen,
  onToggleSuggestion,
  reports,
  activeReportId,
  onToggleReport,
  metadata,
  feedbackData // NEW - Receive feedback data
}) {
  const [copiedMessage, setCopiedMessage] = useState(null);
  const [fileCache, setFileCache] = useState(new Map());

  const API_BASE_URL = process.env.REACT_APP_SERVER_API_URL || 'http://localhost:8080';
  const handleCopyMessage = (text, messageId, messageType) => {
    navigator.clipboard.writeText(text);
    setCopiedMessage({ id: messageId, type: messageType });
    setTimeout(() => {
      setCopiedMessage(null);
    }, 2000);
  };

  // Add this function after handleCopyMessage
  const handleDownloadFile = async (file) => {
    try {
      // Create unique cache key from storagePath
      const cacheKey = file.storagePath;

      let blob;

      // Check if file is already cached
      if (fileCache.has(cacheKey)) {
        console.log('✅ Using cached file:', file.filename);
        blob = fileCache.get(cacheKey);
      } else {
        console.log('📥 Downloading file from server:', file.filename);

        // Extract sessionId, turnNumber, and storedFilename from storagePath
        const pathParts = file.storagePath.split('/');
        const sessionId = pathParts[1];
        const turnNumber = pathParts[2].replace('turn_', '');
        const storedFilename = pathParts[3];

        // Call server download endpoint
        
        const response = await fetch(
          `${API_BASE_URL}/files/download/${sessionId}/${turnNumber}/${storedFilename}`,
          {
            method: 'GET',
          }
        );

        if (!response.ok) {
          throw new Error('Download failed');
        }

        // Create blob from response
        blob = await response.blob();

        // Store in cache for future downloads
        setFileCache(prev => new Map(prev).set(cacheKey, blob));
        console.log('💾 File cached in memory');
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename; // Use original filename for download
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('❌ Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };



  return (
    <Flex direction="column" h="100vh" bg="white">
      {/* Header */}
      <Box
        px="24px"
        py="16px"
        borderBottom="1px solid #ECECEC"
        bg="#FAFAFA"
      >
        <Text fontSize="18px" fontWeight="600" color="#242424">
          {metadata?.chatName || 'Chat Conversation'}
        </Text>
        <Text fontSize="12px" color="#666666">
          Admin View - Read Only
        </Text>
      </Box>

      {/* Messages Container */}
      <Box
        ref={messagesContainerRef}
        flex={1}
        overflowY="auto"
        px="24px"
        py="20px"
        onScroll={onScrollMessages}
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        <VStack spacing="24px" align="stretch">
          {turns.map((turn) => {
            const hasSuggestion = turn.suggestion !== undefined;
            const isSuggestionActive = activeSuggestionTurnId === turn.id && !activeReportId;
            const turnReports = reports[turn.id] || [];
            const feedback = feedbackData[turn.id]; // NEW - Get feedback for this turn

            return (
              <Box key={turn.id}>
                {/* USER MESSAGE */}
                <Flex mb="16px" justify="flex-end">
                  <Box maxW={isRightPaneOpen ? "85%" : "70%"}>
                    <Flex
                      direction="column"
                      bg="#F7F7F7"
                      borderRadius="12px"
                      p="12px 16px"
                      position="relative"
                    >
                      {/* Show current files if they exist */}
                      {turn.currentFiles && turn.currentFiles.length > 0 && (
                        console.log('aig turn.currentfiles', turn.currentFiles),
                        <Flex gap="8px" flexWrap="wrap" mb={turn.userText ? "8px" : "0"}>
                          {turn.currentFiles.map((file, index) => (
                            <Badge
                              key={index}
                              bg="#E3F2FD"
                              color="#1976D2"
                              px="8px"
                              py="4px"
                              borderRadius="6px"
                              fontSize="14px"
                              cursor="pointer"
                              // textDecoration="underline"
                              _hover={{ color: "#004499" }}
                              onClick={() => handleDownloadFile(file)}
                            >
                              📎 {file.name || file.filename}
                            </Badge>
                          ))}
                        </Flex>
                      )}

                      {/* Show text if it exists */}
                      {turn.userText && (
                        <Text
                          fontSize="14px"
                          color="#242424"
                          whiteSpace="pre-wrap"
                          wordBreak="break-word"
                        >
                          {turn.userText}
                        </Text>
                      )}

                      {/* Fallback if neither exists */}
                      {!turn.userText && (!turn.currentFiles || turn.currentFiles.length === 0) && (
                        <Text fontSize="14px" color="#999" fontStyle="italic">
                          [Empty message]
                        </Text>
                      )}
                    </Flex>

                    {/* User Avatar and Copy Button */}
                    <Flex justify="flex-end" align="center" mt="8px" gap="8px">
                      <IconButton
                        icon={
                          copiedMessage?.id === turn.id && copiedMessage?.type === 'user' ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )
                        }
                        onClick={() =>
                          handleCopyMessage(
                            turn.userText || turn.currentFiles?.map(f => f.name).join(', ') || '',
                            turn.id,
                            'user'
                          )
                        }
                        minW="unset"
                        h="24px"
                        w="24px"
                        p="0"
                        _hover={{ bg: "#F3F3F3" }}
                        bg="transparent"
                        aria-label="Copy message"
                      />
                      <Flex
                        w="32px"
                        h="32px"
                        bg="#037A9B"
                        borderRadius="50%"
                        align="center"
                        justify="center"
                        color="white"
                        fontSize="14px"
                        fontWeight="600"
                      >
                        A
                      </Flex>
                    </Flex>
                  </Box>
                </Flex>

                {/* BOT MESSAGE */}
                {turn.botFollowup && (
                  <Box mb="16px">
                    <Flex justify="flex-start">
                      <Box maxW={isRightPaneOpen ? "85%" : "70%"}>
                        <Flex
                          direction="column"
                          bg="#FFFFFF"
                          border="1px solid #ECECEC"
                          borderRadius="12px"
                          p="12px 16px"
                        >
                          <MarkdownRenderer content={turn.botFollowup} />
                        </Flex>

                        {/* Bot Avatar and Action Buttons */}
                        <Flex justify="flex-start" align="center" mt="8px" gap="8px">

                          {/* Copy Button */}
                          <IconButton
                            icon={
                              copiedMessage?.id === turn.id && copiedMessage?.type === 'bot' ? (
                                <Check size={16} />
                              ) : (
                                <Copy size={16} />
                              )
                            }
                            onClick={() => handleCopyMessage(turn.botFollowup, turn.id, 'bot')}
                            minW="unset"
                            h="24px"
                            w="24px"
                            p={0}
                            _hover={{ bg: '#F3F3F3' }}
                            bg="transparent"
                            aria-label="Copy message"
                          />
                        </Flex>

                        {/* NEW - Display Feedback if exists */}
                        {feedback && (
                          <Box
                            mt="12px"
                            // ml="40px"
                            p="12px"
                            bg={feedback.type === 'positive' ? '#E8F5E9' : '#FFEBEE'}
                            border="1px solid"
                            borderColor={feedback.type === 'positive' ? '#4CAF50' : '#EF5350'}
                            borderRadius="8px"
                            maxW="90%"
                          >
                            <HStack spacing={2} mb={feedback.comment ? 2 : 0}>
                              {feedback.type === 'positive' ? (
                                <>
                                  <ThumbsUp size={16} color="#4CAF50" />
                                  <Text fontSize="16px" fontWeight="600" color="#2E7D32">
                                    Positive Feedback
                                  </Text>
                                </>
                              ) : (
                                <>
                                  <ThumbsDown size={16} color="#EF5350" />
                                  <Text fontSize="16px" fontWeight="600" color="#C62828">
                                    Negative Feedback
                                  </Text>
                                </>
                              )}
                            </HStack>

                            {feedback.comment && (
                              <HStack align="start" spacing={2} mt={2}>
                                <MessageSquare size={14} color="#666" style={{ marginTop: '2px', flexShrink: 0 }} />
                                <Text fontSize="16px" color="#666666" fontStyle="italic">
                                  "{feedback.comment}"
                                </Text>
                              </HStack>
                            )}

                            {feedback.timestamp && (
                              <Text fontSize="10px" color="#999" mt={2}>
                                {new Date(feedback.timestamp.toDate ? feedback.timestamp.toDate() : feedback.timestamp).toLocaleString('en-IN')}
                              </Text>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Flex>
                  </Box>
                )}

                {/* SHOW SUGGESTIONS BUTTON */}
                {hasSuggestion && (
                  <>
                    <Flex justify="center" mb="12px">
                      <Button
                        onClick={() => onToggleSuggestion(turn.id)}
                        w="100%"
                        h="75px"
                        borderRadius="12px"
                        bg={isSuggestionActive
                          ? 'linear-gradient(180deg, rgba(255, 200, 235, 1) 0%, rgba(200, 235, 255, 1) 100%)'
                          : 'linear-gradient(180deg, rgba(255, 230, 250, 0.8) 0%, rgba(230, 250, 255, 0.8) 100%)'
                        }
                        border="1px solid"
                        borderColor={isSuggestionActive ? "#FF9CEE" : "#DADADA"}
                        color="#242424"
                        fontSize="14px"
                        fontWeight="medium"
                        textAlign="left"
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-start"
                        position="relative"
                        pl="16px"
                        boxShadow={isSuggestionActive ? '0 4px 12px rgba(207, 0, 138, 0.2)' : 'none'}
                        _hover={{
                          bg: 'linear-gradient(180deg, rgba(255, 220, 245, 1) 0%, rgba(220, 245, 255, 1) 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(207, 0, 138, 0.15)'
                        }}
                        _active={{
                          transform: 'translateY(0)',
                          boxShadow: '0 2px 6px rgba(207, 0, 138, 0.1)'
                        }}
                        transition="all 0.2s ease"
                      >
                        <Text fontSize="14px" color="#666666">
                          Suggestions generated
                        </Text>
                        <Flex
                          position="absolute"
                          right="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          align="center"
                          justify="center"
                          w="32px"
                          h="32px"
                          bg="white"
                          borderRadius="50%"
                          boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                        >
                          <ArrowRight size={18} color="#CF008A" />
                        </Flex>
                      </Button>
                    </Flex>

                    {/* Report Buttons */}
                    {turnReports.map((report) => {
                      const isReportActive = activeReportId?.turnId === turn.id && activeReportId?.version === report.version;
                      const reportLabel = report.version === 1 ? 'Report' : `Report V${report.version}`;

                      return (
                        <Flex key={report.version} justify="center" mb="12px">
                          <Button
                            onClick={() => onToggleReport(turn.id, report.version)}
                            w="100%"
                            h="75px"
                            borderRadius="12px"
                            bg={isReportActive
                              ? 'linear-gradient(180deg, rgba(255, 200, 235, 1) 0%, rgba(200, 235, 255, 1) 100%)'
                              : 'linear-gradient(180deg, rgba(255, 230, 250, 0.8) 0%, rgba(230, 250, 255, 0.8) 100%)'
                            }
                            border="1px solid"
                            borderColor={isReportActive ? "#FF9CEE" : "#DADADA"}
                            color="#242424"
                            fontSize="14px"
                            fontWeight="medium"
                            textAlign="left"
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            position="relative"
                            pl="16px"
                            boxShadow={isReportActive ? '0 4px 12px rgba(207, 0, 138, 0.2)' : 'none'}
                            _hover={{
                              bg: 'linear-gradient(180deg, rgba(255, 220, 245, 1) 0%, rgba(220, 245, 255, 1) 100%)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(207, 0, 138, 0.15)'
                            }}
                            _active={{
                              transform: 'translateY(0)',
                              boxShadow: '0 2px 6px rgba(207, 0, 138, 0.1)'
                            }}
                            transition="all 0.2s ease"
                          >
                            <Text fontSize="14px" color="#666666">
                              Report generated
                            </Text>
                            <Text fontSize="14px" fontWeight="600" color="#242424">
                              V{report.version}
                            </Text>
                            <Flex
                              position="absolute"
                              right="16px"
                              top="50%"
                              transform="translateY(-50%)"
                              align="center"
                              justify="center"
                              w="32px"
                              h="32px"
                              bg="white"
                              borderRadius="50%"
                              boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                            >
                              <ArrowRight size={18} color="#CF008A" />
                            </Flex>
                          </Button>
                        </Flex>
                      );
                    })}
                  </>
                )}
              </Box>
            );
          })}
        </VStack>
      </Box>

      {/* Footer - Read Only Notice */}
      <Box
        px="24px"
        py="12px"
        borderTop="1px solid #ECECEC"
        bg="#FAFAFA"
      >
        <Text fontSize="12px" color="#666666" textAlign="center">
          🔒 Admin View - Read Only Mode
        </Text>
      </Box>
    </Flex>
  );
}
